import React, { useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { useFormik } from 'formik';
import { classNames } from 'primereact/utils';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import bgImage from '../../src/assets/images/bg.png';
import { handlePostRequest } from '../service/PostTemplate';
import { endPoints } from '../Config';

const ForgetPassword = () => {
    const [loading, setLoading] = useState(false);
    const [emailVerified, setEmailVerified] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Validation for email
    const emailValidationSchema = Yup.object().shape({
        email: Yup.string()
            .matches(/^[^\s]+(\s[^\s]+)*$/, 'Leading and trailing spaces are not allowed')
            .email('Invalid email')
            .required('Email is required')
    });

    // Validation for OTP
    const otpValidationSchema = Yup.object().shape({
        otp: Yup.string()
            .matches(/^[^\s]+(\s[^\s]+)*$/, 'Leading and trailing spaces are not allowed')
            .required('OTP is required')
    });

    const formik = useFormik({
        initialValues: { email: '', otp: '', userId: '' },
        validationSchema: !emailVerified ? emailValidationSchema : otpValidationSchema,
        onSubmit: async (values) => {
            try {
                setLoading(true);

                if (!emailVerified) {
                    // ✅ Step 1: Verify email using verifyMerchantUser endpoint
                    const emailData = { email: values.email };
                    const response = await dispatch(handlePostRequest(emailData, endPoints.verifyMerchantUser, true));

                    if (response?.status === 200 && response?.data?.data) {
                        toast.success(response?.data?.message || 'Email verified', { autoClose: 2000, toastId: 'email-verified' });
                        setEmailVerified(true);
                        formik.setFieldValue('userId', response?.data?.data?.userId);
                    } else {
                        toast.error(response?.data?.message || 'Email not registered', { autoClose: 2000 });
                    }
                } else {
                    // ✅ Step 2: Verify OTP
                    const otpData = {
                        email: values.email,
                        otp: values.otp,
                        userId: values.userId
                    };
                    const response = await dispatch(handlePostRequest(otpData, endPoints.verifyOtp, true));

                    if (response?.status === 200) {
                        toast.success('OTP verified successfully!', { autoClose: 2000 });
                        navigate('/login'); // redirect to login page
                    } else {
                        toast.error(response?.data?.message || 'Invalid OTP', { autoClose: 2000 });
                    }
                }
            } catch (error) {
                toast.error(error.response?.data?.message || 'Something went wrong', { autoClose: 2000 });
            } finally {
                setLoading(false);
            }
        }
    });

    // ✅ Resend OTP
    const resendOtp = async () => {
        try {
            setLoading(true);
            const emailData = { email: formik.values.email };
            const response = await dispatch(handlePostRequest(emailData, endPoints.verifyMerchantUser, true));

            if (response?.status === 200) {
                toast.success('OTP has been resent to your email', { autoClose: 2000 });
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to resend OTP', { autoClose: 2000 });
        } finally {
            setLoading(false);
        }
    };

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;

    return (
        <div className="login-body">
            <div className="login-image">
                <img src={bgImage} alt="atlantis" />
            </div>
            <div className="login-panel p-fluid" style={{ background: '#fff' }}>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        formik.handleSubmit(e);
                    }}
                >
                    {!emailVerified ? (
                        <div className="flex flex-column">
                            <div className="flex align-items-center mb-6 logo-container">
                                <p className="Signin__Heading">Verify Email</p>
                            </div>
                            <div className="form-container">
                                <div className="flex flex-column w-12">
                                    <span className="p-input-icon-left">
                                        <i className="pi pi-envelope"></i>
                                        <InputText id="email" name="email" value={formik.values.email} placeholder="Enter Email" onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('email') }, 'Login__Input')} />
                                        {getFormErrorMessage('email')}
                                    </span>
                                </div>
                            </div>
                            <div className="button-container mt-4">
                                <Button type="submit" label={loading ? 'Please wait...' : 'Submit'} icon={loading } iconPos="right" className="Login__Button" disabled={loading} />
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-column">
                            <div className="flex align-items-center mb-6 logo-container">
                                <p className="Signin__Heading">Verify OTP</p>
                            </div>
                            <div className="form-container">
                                <div className="flex flex-column w-12">
                                    <span className="p-input-icon-left">
                                        {/* <i className="pi pi-key"></i> */}
                                        <InputText id="otp" name="otp" value={formik.values.otp} placeholder="Enter OTP" onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('otp') }, 'Login__Input')} />
                                        {getFormErrorMessage('otp')}
                                    </span>
                                </div>
                                <div className="Remember__Forget flex justify-content-end flex-wrap">
                                    <div className="mt-1">
                                        <label style={{ cursor: 'pointer' }} onClick={resendOtp} className="forgot_label font-bold">
                                            Resend OTP
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div className="button-container mt-4">
                                <Button type="submit" label={loading ? 'Please wait...' : 'Submit'} icon={loading} iconPos="right" className="Login__Button" disabled={loading} />
                            </div>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default ForgetPassword;
